import React, { FC } from 'react';
import styled from 'styled-components';

import { colors, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../constants';
import { pxToRem } from '../utilities/utility';

import { OptionButton, PrimaryButton } from './Button';
import Heading from './Heading';
import { ModalProps, ModalWrapper } from './ModalWrapper';

// Styles -----------------------------------------------------------------------------------------
const TcpaModalContent = styled.div`
  position: relative;
  max-width: 751px;
  min-width: 340px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 1.5rem;
  background: #ffffff;
  overflow: hidden;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
  @media (max-height: ${MOBILE_BREAKPOINT}) {
    width: 35rem;
    padding: 1.5rem;
  }
`;
TcpaModalContent.displayName = 'TcpaModalContent';

const ModalHeading = styled(Heading)`
  padding: 1.18rem 0rem;
  white-space: pre-line;
  text-align: left;
  font-family: Inter;
`;
ModalHeading.displayName = 'ModalHeading';
const BodyContainer = styled.div`
  border: 0.0625rem solid #d8d8d8;
  border-radius: 0.375rem;
  height: 100%;
  overflow: auto;
  min-height: 6.25rem;
`;
BodyContainer.displayName = 'BodyContainer';

const BodyText = styled.p`
  padding: 0 1rem;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  font-family: Inter;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 2rem;
  }
`;

const BoldText = styled(BodyText)`
  font-weight: 700;
`;
BoldText.displayName = 'BoldText';

BodyText.displayName = 'BodyText';

const ButtonContainer = styled.div`
  margin-top: 1.18rem;
  margin-bottom: 1.87rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column-reverse;
    gap: 12px;
    align-items: center;
  }
`;
ButtonContainer.displayName = 'ButtonContainer';

const AcceptButton = styled(PrimaryButton)`
  font-size: ${pxToRem(18)};
  border: 0px;
  padding: 1rem 3rem;
  font-family: Inter;
`;
AcceptButton.displayName = 'AcceptButton';

const DisagreeButton = styled(OptionButton)`
  background-color: #f2f2f2;
  color: ${colors.TEXT_INPUT}
  font-size: ${pxToRem(16)};
  font-family: Inter;
  border: 0px;
  padding: 1rem 3rem;
  height: 3.5rem;
`;
DisagreeButton.displayName = 'DisagreeButton';

// Define a styled component for the iframe
const StyledIframe = styled.iframe`
  width: 100%;
  min-height: 6.25rem;
  @media (min-width: ${TABLET_BREAKPOINT}) {
    width: 40.625rem;
    height: 18.125rem;
  }
`;

type TcpaModalProps = ModalProps & {
  title: string;
  contentUrl: string;
  onDisagree: Function;
};

// Component --------------------------------------------------------------------------------------
export const TcpaModal: FC<TcpaModalProps> = ({
  showModal,
  setShowModal,
  onDisagree,
  title = 'TCPA Authorization',
  contentUrl,
}) => (
  <ModalWrapper
    id="tcpa-modal"
    showModal={showModal}
    setShowModal={setShowModal}
    shouldReturnFocusAfterClose={false}
    borderRadius="1rem"
  >
    <TcpaModalContent
      tabIndex={-1}
      aria-labelledby="modal-container"
      aria-modal="true"
      role="dialog"
      data-testid="tcpa-modal"
    >
      <ModalHeading id="modal-heading">{title}</ModalHeading>
      <BodyContainer>
        <StyledIframe src={contentUrl} title={title} />
      </BodyContainer>
      <ButtonContainer>
        <DisagreeButton
          selected={false}
          id="disagree-tcpa-button"
          aria-label="disagree-to-marketing-communications"
          onClick={onDisagree}
          type="button"
          role="button"
        >
          I do not agree
        </DisagreeButton>
        <AcceptButton
          id="back-to-application-button"
          onClick={() => setShowModal(false)}
          aria-label="agree and back to application"
          type="button"
          role="button"
        >
          Back to application
        </AcceptButton>
      </ButtonContainer>
    </TcpaModalContent>
  </ModalWrapper>
);
